/* eslint-disable complexity */
import React, {
    useReducer, useEffect, useCallback, useRef, useState
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import deliveryOptionsUtils from 'utils/DeliveryOptions';
import { Grid, Text, Link } from 'components/ui';
import { space, mediaQueries } from 'style/config';
import mediaUtils from 'utils/Media';
import skuUtils from 'utils/Sku';
import AddToBasketButton from 'components/AddToBasketButton';
import RougeRewardButton from 'components/ProductPage/Type/RewardProduct/RougeRewardButton/RougeRewardButton';
import basketUtils from 'utils/Basket';
import basketConstants from 'constants/Basket';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import analyticsConstants from 'analytics/constants';
import languageLocale from 'utils/LanguageLocale';
import SkuQuantity from 'components/Product/SkuQuantity';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import DeliveryFrequency from 'utils/DeliveryFrequency';
import productPageBindings from 'analytics/bindingMethods/pages/productPage/productPageSOTBindings';
import constantsSkuQuantity from 'components/Product/SkuQuantity/constants';
import skuHelpers from 'utils/skuHelpers';
import helpersUtils from 'utils/Helpers';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';
import Empty from 'constants/empty';
import ErrorDisplay from 'components/GlobalModals/ChooseOptionsModal/ErrorDisplay';
import { CTA } from 'constants/constructorConstants';
import UI from 'utils/UI';
import Location from 'utils/Location';
import addToBasketActions from 'actions/AddToBasketActions';
import RwdBasketActions from 'actions/RwdBasketActions/RwdBasketActions';
import basketSelector from 'selectors/basket/basketSelector';
import { authSelector } from 'selectors/auth/authSelector';
import itemsByBasketSelector from 'selectors/basket/itemsByBasket/itemsByBasketSelector';
import preferredStoreInfoSelector from 'selectors/user/preferredStoreInfoSelector';
import { showIncreaseATBButtonOnPdpSelector } from 'viewModel/selectors/testTarget/showIncreaseATBButtonOnPdpSelector';
import CallToActionEN from 'components/ProductPage/CallToAction/locales/CallToAction_en_US';
import CallToActionFR from 'components/ProductPage/CallToAction/locales/CallToAction_fr_CA';
import { initialState, reducer } from 'components/ProductPage/CallToAction/callToActionReducer';
import { styles } from 'components/ProductPage/CallToAction/callToActionStyles';
import BiExclusiveMsg from 'components/ProductPage/CallToAction/BiExclusiveMsg';
import DownloadText from 'components/ProductPage/CallToAction/DownloadText';
import ApplePayButton from 'components/ApplePayButton/ApplePayButton';
import ApplePay from 'services/ApplePay';

const {
    CONTEXT: { BASKET_PRODUCT }
} = analyticsConstants;
const { ADD_TO_BASKET_TYPES } = basketUtils;
const { BasketType, API_BASKET_TYPE } = basketConstants;
const { getLocalisedDataFromResource } = languageLocale;
const { MIN_WIDTH } = constantsSkuQuantity;
const { Media } = mediaUtils;
const { formatFrequencyType } = DeliveryFrequency;
const { isNDDNewDeliveryName } = deliveryOptionsUtils;
const BUTTON_WIDTH = ['100%', null, 212];
const BUTTON_WIDTH_WITH_QUANTITY_PICKER = ['100%', null, 282];
const BASKET_TYPE_TO_TEXT = { [BasketType.BOPIS]: 'storePickup', [BasketType.SameDay]: 'sameDayDelivery', [BasketType.Standard]: 'standardShipping' };
const getText = getLocalisedDataFromResource(CallToActionEN, CallToActionFR);
const { deferTaskExecution } = helpersUtils;

// PAY-7285: environment-level eligibility for the 1-click Apple Pay button —
// feature flag ON + US locale. These are stable for the whole session, so this also
// gates the capability check in the mount effect.
const isOneClickApplePayEligible = () => ApplePay.isOneClickApplePayEnabled() && languageLocale.isUS();

const CallToAction = props => {
    const {
        product,
        sku,
        basketType,
        loveIcon,
        loveButton,
        addToBasketDataAt,
        addToBasketDataAtSm,
        addToBasketCallback,
        isCustomSets,
        isSameDayAvailable,
        productExtraDetailsLoaded,
        displayQuantityPickerInATB,
        autoReplenishChecked,
        autoReplenishQuantity,
        isKohlsStore,
        isChooseOptionsModal = false,
        analyticsContext,
        rootContainerName,
        stickyError = null,
        accountForBottomNav,
        pageName,
        isSmallView,
        showPdpCompare,
        replenishmentFreqNum,
        replenishmentFreqType,
        isAutoReplenMostCommon,
        updateFrequencyModal,
        onAutoReplenishQuantityChange,
        customStyle,
        increaseATBButtonOnPdpStyle
    } = props;

    const dispatch = useDispatch();
    const basket = useSelector(basketSelector, shallowEqual);
    const auth = useSelector(authSelector, shallowEqual);
    const itemsByBasket = useSelector(itemsByBasketSelector, shallowEqual);
    const preferredStoreInfo = useSelector(preferredStoreInfoSelector, shallowEqual);
    const increaseATBButtonOnPdp = useSelector(showIncreaseATBButtonOnPdpSelector);
    const removeItemFromBasket = useCallback((...args) => dispatch(addToBasketActions.removeItemFromBasket(...args)), [dispatch]);
    const updateSkuQuantity = useCallback((...args) => dispatch(RwdBasketActions.updateSkuQuantity(...args)), [dispatch]);

    const [state, localDispatch] = useReducer(reducer, initialState);
    // PAY-7285: Apple Pay device capability (ENABLED/DISABLED/HIDDEN)
    const [applePayType, setApplePayType] = useState(ApplePay.TYPES.HIDDEN);
    const prevPropsRef = useRef({
        basket,
        itemsByBasket,
        sku,
        basketType,
        auth,
        autoReplenishChecked,
        autoReplenishQuantity,
        product
    });
    const stateRef = useRef(state);
    stateRef.current = state;

    const getApiBasketType = useCallback(type => (type === null ? API_BASKET_TYPE.standard : API_BASKET_TYPE[type]), []);

    const setIsSkuInBasket = useCallback(() => {
        const basketItems = (basketType === BasketType.BOPIS ? basket?.pickupBasket?.items : basket?.items) || Empty.Array;
        const isSkuInBasket = Sephora.configurationSettings.calculateIsInBasketFlagOnChannel
            ? skuHelpers.isInBasket(sku.skuId, { items: basketItems })
            : sku.isInBasket;
        localDispatch({ type: 'SET', payload: { isSkuInBasket } });
    }, [basket, basketType, sku]);

    const getBasketItems = useCallback(() => {
        const isBopis = basketType === BasketType.BOPIS;
        const apiBasketType = getApiBasketType(basketType);
        const basketItems = isBopis ? basket?.pickupBasket : itemsByBasket.find(b => b.basketType === apiBasketType);

        return { isBopis, basketItems };
    }, [basket, basketType, itemsByBasket, getApiBasketType]);

    const getSkuItem = useCallback(() => {
        const { basketItems } = getBasketItems();

        return basketItems?.items.find(item => item.sku.skuId === sku.skuId) || null;
    }, [getBasketItems, sku]);

    const refreshSkuQuantity = useCallback(
        basketChanged => {
            if (autoReplenishChecked && !isChangeQuantityEnabled()) {
                return;
            }

            const isBopis = basketType === BasketType.BOPIS;

            if (basketChanged && isBopis && !basket?.pickupBasket?.items?.length) {
                localDispatch({ type: 'SET', payload: { skuQuantity: 1, shouldShowQuantityPicker: false } });

                return;
            }

            if (itemsByBasket?.length === 0 && !isBopis) {
                localDispatch({
                    type: 'SET',
                    payload: basketChanged ? { skuQuantity: 1, shouldShowQuantityPicker: false } : { shouldShowQuantityPicker: false }
                });

                return;
            }

            if (stateRef.current.skuQuantity === 0 && !basketChanged) {
                localDispatch({ type: 'SET', payload: { skuQuantity: 1, shouldShowQuantityPicker: false } });

                return;
            }

            const skuItem = getSkuItem();
            const skuQuantity = skuItem?.qty || 0;
            localDispatch({ type: 'SET', payload: { shouldShowQuantityPicker: skuQuantity > 0, ...(skuQuantity > 0 && { skuQuantity }) } });
        },
        [autoReplenishChecked, basketType, basket, itemsByBasket, getSkuItem]
    );

    const fireAnalytics = useCallback(
        qty => {
            pdpAnalytics.trackATBClick(sku, qty, displayQuantityPickerInATB);
        },
        [sku, displayQuantityPickerInATB]
    );

    const handleSkuQuantity = useCallback(
        value => {
            localDispatch({ type: 'SET', payload: { skuQuantity: value, isSkuQuantityChanged: true } });

            if (autoReplenishChecked && isChangeQuantityEnabled()) {
                onAutoReplenishQuantityChange?.(value);
            } else {
                updateFrequencyModal({ quantity: value });
            }

            fireAnalytics(value);
            productPageBindings.productQuantityChange({ quantity: value, skuId: sku?.skuId });
        },
        [autoReplenishChecked, onAutoReplenishQuantityChange, updateFrequencyModal, fireAnalytics, sku]
    );

    const handleQuantityChange = useCallback(
        quantity => {
            const { isBopis } = getBasketItems();
            const skuItem = getSkuItem();
            const shippingMethod = getApiBasketType(basketType);
            const updatedSku = { ...sku, shippingMethod, ...(quantity === '0' && { changedQuantity: skuItem?.qty ?? 0 }) };

            if (quantity === '0') {
                // Immediate: update UI state for instant visual feedback before the paint
                localDispatch({ type: 'SET', payload: { skuQuantity: 0, isSkuQuantityChanged: true, shouldShowQuantityPicker: false } });
                // Deferred: Redux dispatch triggers re-renders across all basket subscribers — defer to unblock paint
                deferTaskExecution(() => {
                    removeItemFromBasket({ sku: updatedSku }, true, false, isBopis, basket?.appliedPromotions, true);
                });

                if (autoReplenishChecked && isChangeQuantityEnabled()) {
                    onAutoReplenishQuantityChange?.('1');
                }
            } else {
                localDispatch({ type: 'SET', payload: { skuQuantity: quantity, isSkuQuantityChanged: true } });
                // Deferred: same reason — basket subscriber re-renders should not block the input paint
                deferTaskExecution(() => {
                    updateSkuQuantity({
                        newQty: quantity,
                        sku: updatedSku,
                        isBopis,
                        commerceId: skuItem.commerceId,
                        isAutoReplenish: autoReplenishChecked
                    });
                });

                if (autoReplenishChecked && isChangeQuantityEnabled()) {
                    onAutoReplenishQuantityChange?.(quantity);
                }
            }
        },
        [
            getBasketItems,
            getSkuItem,
            getApiBasketType,
            basketType,
            sku,
            removeItemFromBasket,
            updateSkuQuantity,
            basket,
            autoReplenishChecked,
            onAutoReplenishQuantityChange
        ]
    );

    const playUpdateAnimation = useCallback(() => {
        localDispatch({ type: 'SET', payload: { showUpdateMessage: true } });
        setTimeout(() => localDispatch({ type: 'SET', payload: { showUpdateMessage: false } }), 1500);
    }, []);

    const scrollToSimilar = useCallback(e => {
        e.preventDefault();
        UI.scrollTo({ elementId: 'similar' });
        pdpAnalytics.trackLinkClick({ actionInfo: 'view similar products' });
    }, []);

    const getSecondaryText = useCallback(
        ({ sameDayTitle, supportedBasketType, isBiReward }) => {
            if (isBiReward) {
                return undefined;
            }

            if (autoReplenishChecked && sku.isReplenishmentEligible) {
                return 'autoReplenish';
            }

            if (supportedBasketType === BasketType.SameDay) {
                if (isNDDNewDeliveryName()) {
                    return 'fastDelivery';
                }

                return sameDayTitle ? 'sameDayCustomDelivery' : 'sameDayDelivery';
            }

            if (supportedBasketType === BasketType.Standard) {
                return 'standardShipping';
            }

            return BASKET_TYPE_TO_TEXT[supportedBasketType];
        },
        [autoReplenishChecked, sku]
    );

    useEffect(() => {
        setIsSkuInBasket();
    }, []);

    // PAY-7285: resolve Apple Pay device capability for the PDP 1-click button.
    // Skipped entirely when not eligible (flag/US) or not mWeb, so off-channel = no work.
    useEffect(() => {
        if (isSmallView && isOneClickApplePayEligible()) {
            ApplePay.checkApplePayments().then(type => setApplePayType(type));
        }
    }, [isSmallView]);

    useEffect(() => {
        const prev = prevPropsRef.current;

        if (product?.productDetails?.productId !== prev.product?.productDetails?.productId) {
            localDispatch({ type: 'RESET_QTY' });
        }

        if (prev.autoReplenishQuantity !== autoReplenishQuantity) {
            localDispatch({ type: 'SET', payload: { isSkuQuantityChanged: false } });
        }

        if (
            basket !== prev.basket ||
            itemsByBasket !== prev.itemsByBasket ||
            sku.skuId !== prev.sku?.skuId ||
            basketType !== prev.basketType ||
            auth?.profileStatus !== prev.auth?.profileStatus ||
            autoReplenishChecked !== prev.autoReplenishChecked
        ) {
            setIsSkuInBasket();
            refreshSkuQuantity(basketType !== prev.basketType);

            if (autoReplenishChecked && !isChangeQuantityEnabled()) {
                localDispatch({ type: 'SET', payload: { shouldShowQuantityPicker: false } });
            }
        }

        prevPropsRef.current = {
            basket,
            itemsByBasket,
            sku,
            basketType,
            auth,
            autoReplenishChecked,
            autoReplenishQuantity,
            product
        };
    }, [basket, itemsByBasket, sku, basketType, auth, autoReplenishChecked, autoReplenishQuantity, product, setIsSkuInBasket, refreshSkuQuantity]);

    const {
        skuQuantity, isSkuQuantityChanged, shouldShowQuantityPicker, isSkuInBasket, showUpdateMessage
    } = state;
    const hasAcceleratedPromo = !!sku?.acceleratedPromotion;
    const arQtyPickerEnabled = autoReplenishChecked && isChangeQuantityEnabled() && !hasAcceleratedPromo;

    const updatedQuantity = autoReplenishChecked && !isSkuQuantityChanged && !shouldShowQuantityPicker ? autoReplenishQuantity : skuQuantity;

    if (!sku) {
        return null;
    }

    const skuType = skuUtils.getProductType(sku);
    const isReward = skuUtils.isBiReward(sku);
    const isRougeRewardCard = skuType === skuUtils.skuTypes.ROUGE_REWARD_CARD;
    const isAppExclusive = skuUtils.isAppExclusive(sku);
    const hasBiExclusive = skuUtils.isBiExclusive(sku) && !skuHelpers.isBiQualify(sku);
    const isButtonDisabled =
        (!isCustomSets && !productExtraDetailsLoaded) ||
        (basketType === BasketType.SameDay && !isSameDayAvailable) ||
        (!sku.isReplenishmentEligible && autoReplenishChecked);
    const hasSelectedKohlsStore = basketType === BasketType.BOPIS && isKohlsStore;
    const isSkuOutOfStockByBasketType = skuHelpers.isSkuOutOfStockByBasketType(sku, basketType, isChooseOptionsModal);
    const isQuantityPickerSkuAllowed =
        !isSkuOutOfStockByBasketType &&
        !skuUtils.isGiftCard(sku) &&
        !skuUtils.isGwp(sku) &&
        !skuUtils.isBiReward(sku) &&
        !isCustomSets &&
        !hasSelectedKohlsStore &&
        !skuHelpers.isProductDisabled(sku) &&
        !skuUtils.isAppExclusive(sku);
    const shouldDisplayQuantityPickerInATB =
        displayQuantityPickerInATB && !isButtonDisabled && (!autoReplenishChecked || arQtyPickerEnabled) && isQuantityPickerSkuAllowed;
    const shouldDisplayViewSimilarLink = Location.isProductPage() && showPdpCompare;
    const buttonWidthWithQuantityPicker = isChooseOptionsModal ? ['100%', 360] : BUTTON_WIDTH_WITH_QUANTITY_PICKER;
    const { isExternallySellable } = sku;
    const isSdd = basketType === BasketType.SameDay;
    const isBopis = basketType === BasketType.BOPIS;
    const isBiReward = skuUtils?.isBiReward(product?.currentSku);
    const sameDayTitle = product?.sku?.sameDayTitle;
    const supportedBasketType = isCustomSets || isBiReward ? BasketType.Standard : basketType;
    const secondaryText = getSecondaryText({ sameDayTitle, supportedBasketType, isBiReward });
    const dynamicText = autoReplenishChecked
        ? [`${replenishmentFreqNum} ${formatFrequencyType(replenishmentFreqNum, replenishmentFreqType)}`]
        : sameDayTitle
            ? [sameDayTitle]
            : '';
    const secondaryTextLiteral = getText(secondaryText, dynamicText);
    const customSkuQuantityStyles = increaseATBButtonOnPdp ? styles.increaseATBButtonOnPdp : {};
    const isApplePaySupportedFulfillment =
        supportedBasketType === BasketType.Standard || supportedBasketType === BasketType.BOPIS || supportedBasketType === BasketType.SameDay;
    const isBasketEmpty = basketUtils.getTotalCount(basket) === 0;

    // 1-click Apple Pay button is disabled — not hidden — when pickup is not offered for
    // the selected store/SKU (reservation not offered, BOPIS/ROPIS disabled, or the
    // preferred store supports neither).
    const isBopisUnavailable =
        supportedBasketType === BasketType.BOPIS &&
        (skuUtils.isReservationNotOffered(sku) ||
            (!Sephora.configurationSettings.isBOPISEnabled && preferredStoreInfo?.isBopisable) ||
            (!Sephora.configurationSettings.isROPISEnabled && preferredStoreInfo?.isRopisable) ||
            (!preferredStoreInfo?.isBopisable && !preferredStoreInfo?.isRopisable));
    const isSameDayUnavailable = supportedBasketType === BasketType.SameDay && !isSameDayAvailable;
    const showOneClickApplePayButton =
        isOneClickApplePayEligible() &&
        isBasketEmpty &&
        isApplePaySupportedFulfillment &&
        !isReward &&
        isSmallView &&
        applePayType === ApplePay.TYPES.ENABLED;

    const button = (dataAt, isSticky) =>
        isRougeRewardCard ? (
            <RougeRewardButton
                product={product}
                dataAt={dataAt}
                buttonWidth={BUTTON_WIDTH}
                biExclusiveMsg={hasBiExclusive ? <BiExclusiveMsg sku={sku} /> : null}
                addToBasketCallback={addToBasketCallback}
            />
        ) : (
            <Grid
                gap={isChooseOptionsModal ? [2, null, 0] : [4, null, loveButton ? 2 : 4]}
                columns={!shouldDisplayViewSimilarLink ? [null, null, 'auto 1fr auto'] : null}
                alignItems='center'
                {...(shouldDisplayViewSimilarLink && { gridAutoFlow: [null, 'column'], gridAutoColumns: [null, 'max-content'] })}
            >
                {isExternallySellable ? null : (
                    // PAY-7285: Add to Basket (left) + 1-click Apple Pay (right), always qty 1
                    <div css={{ display: 'flex', gap: space[2], alignItems: 'center' }}>
                        <div css={{ flex: 1, minWidth: 0, ...(shouldDisplayQuantityPickerInATB && { position: 'relative' }) }}>
                            {shouldDisplayQuantityPickerInATB && (
                                <div css={styles.quantityPicker(shouldShowQuantityPicker || showUpdateMessage)}>
                                    <SkuQuantity
                                        secondaryTextLiteral={secondaryTextLiteral}
                                        basketType={basketType}
                                        currentSku={sku}
                                        skuQuantity={updatedQuantity}
                                        handleSkuQuantity={shouldShowQuantityPicker ? handleQuantityChange : handleSkuQuantity}
                                        isNotSelectable={autoReplenishChecked && !arQtyPickerEnabled}
                                        isSdd={isSdd}
                                        isBopis={isBopis}
                                        isChooseOptionsModal={isChooseOptionsModal}
                                        playUpdateAnimation={playUpdateAnimation}
                                        showUpdateMessage={showUpdateMessage}
                                        inputProps={{ chevronColor: 'white' }}
                                        customStyle={
                                            shouldShowQuantityPicker || showUpdateMessage
                                                ? { ...styles.skuQuantityAlt(buttonWidthWithQuantityPicker), ...customSkuQuantityStyles }
                                                : { ...styles.skuQuantity, ...customSkuQuantityStyles }
                                        }
                                        {...(shouldShowQuantityPicker && {
                                            startFromZero: shouldShowQuantityPicker,
                                            displayShowQuantityPicker: shouldShowQuantityPicker
                                        })}
                                    />
                                </div>
                            )}
                            {!shouldShowQuantityPicker && !showUpdateMessage && (
                                <AddToBasketButton
                                    customStyle={customStyle}
                                    increaseATBButtonOnPdpStyle={increaseATBButtonOnPdpStyle}
                                    isSticky={isSticky}
                                    data-at={Sephora.debug.dataAt(dataAt)}
                                    data-cnstrc-btn={CTA.ADD_TO_CART}
                                    skuQuantity={updatedQuantity}
                                    width={shouldDisplayQuantityPickerInATB ? buttonWidthWithQuantityPicker : BUTTON_WIDTH}
                                    paddingLeft={shouldDisplayQuantityPickerInATB && MIN_WIDTH.BASE}
                                    product={product}
                                    productId={product.productId}
                                    sku={sku}
                                    displayQuantityPickerInATB={shouldDisplayQuantityPickerInATB}
                                    quantity={updatedQuantity}
                                    analyticsContext={analyticsContext || BASKET_PRODUCT}
                                    rootContainerName={rootContainerName}
                                    pageName={pageName}
                                    callback={addToBasketCallback}
                                    isCustomSets={isCustomSets}
                                    replenishmentFreqNum={replenishmentFreqNum}
                                    replenishmentFreqType={replenishmentFreqType}
                                    autoReplenishChecked={autoReplenishChecked}
                                    isAutoReplenMostCommon={isAutoReplenMostCommon}
                                    basketType={basketType}
                                    ctaTwoLines={true}
                                    isChooseOptionsModal={isChooseOptionsModal}
                                    {...(isReward
                                        ? {
                                            variant: isSkuInBasket ? ADD_TO_BASKET_TYPES.PRIMARY : ADD_TO_BASKET_TYPES.SPECIAL,
                                            disabled: isSkuInBasket ? false : !sku.isEligible,
                                            quantity: product.currentSkuQuantity
                                        }
                                        : {
                                            key: `addToBasketButton_${isButtonDisabled}`,
                                            variant: ADD_TO_BASKET_TYPES.SPECIAL,
                                            disabled: isButtonDisabled,
                                            basketType
                                        })}
                                />
                            )}
                        </div>
                        {showOneClickApplePayButton && (
                            <div css={{ flexShrink: 0 }}>
                                <ApplePayButton
                                    isPdpFlow={true}
                                    sku={sku}
                                    fulfillmentType={supportedBasketType}
                                    quantity={updatedQuantity}
                                    isApplePayPayment={
                                        isSkuOutOfStockByBasketType || isBopisUnavailable || isAppExclusive || isSameDayUnavailable
                                            ? ApplePay.TYPES.DISABLED
                                            : applePayType
                                    }
                                    iconOnly={true}
                                    width={[130, null, 160]}
                                />
                            </div>
                        )}
                    </div>
                )}
                {(hasBiExclusive || isAppExclusive) && !isExternallySellable && (
                    <Text
                        is='p'
                        lineHeight='tight'
                        textAlign={['center', null, 'left']}
                        fontSize={['xs', 'sm']}
                        maxWidth={[null, null, '16em']}
                        data-at={Sephora.debug.dataAt('product_label')}
                        css={{ [mediaQueries.smMax]: { order: -1 } }}
                    >
                        {isAppExclusive ? <DownloadText /> : <BiExclusiveMsg sku={sku} />}
                    </Text>
                )}
                {(loveIcon || loveButton) && (
                    <Media
                        greaterThan='sm'
                        css={loveIcon && isExternallySellable ? { paddingTop: space[4] } : { marginLeft: space[2], marginRight: space[3] }}
                    >
                        {loveIcon || loveButton}
                    </Media>
                )}
                {shouldDisplayViewSimilarLink && isSkuOutOfStockByBasketType && (
                    <Link
                        textAlign={['center', null]}
                        paddingBottom={isSmallView && 4}
                        color='blue'
                        href='#similar'
                        onClick={scrollToSimilar}
                        children={getText('viewSimilarProducts')}
                    />
                )}
            </Grid>
        );

    return (
        <React.Fragment>
            <Media greaterThan={isChooseOptionsModal ? 'xs' : 'sm'}>{button(addToBasketDataAt, false)}</Media>
            <Media lessThan={isChooseOptionsModal ? 'sm' : 'md'}>
                {isExternallySellable ? null : (
                    <StickyFooter accountForBottomNav={accountForBottomNav}>
                        {stickyError && <ErrorDisplay apiError={stickyError} />}
                        {button(addToBasketDataAtSm, true)}
                    </StickyFooter>
                )}
            </Media>
        </React.Fragment>
    );
};

export default wrapFunctionalComponent(CallToAction, 'CallToAction');
